import { isAxiosError } from 'axios';
import { buildGarageAddress } from '@/lib/entrance-utils';

/**
 * Matches a raw payment-gateway error blob the API may surface verbatim, e.g.
 * `Stripe error 402 PAYMENT_REQUIRED: {"error":{...}}`. The API now sanitizes
 * these at the source, so this is a defensive guard for the deploy window (old
 * API still in rotation) and for the few call sites that read `error.message`
 * directly. Scoped to the exact `<Vendor> error <status>: {json}` prefix so it
 * never rewrites a legitimate message that merely mentions a gateway.
 */
const GATEWAY_ERROR_BLOB = /^(?:Stripe|Windcave|Clover|PayPal|Square) error \d{3}\b/;

/**
 * If `message` is a raw gateway error blob, return only the inner
 * `error.message` (the sole user-safe field) — never the JSON, request log URL,
 * account id, or request id. Unparseable blobs return the fallback. Non-blob
 * messages pass through unchanged.
 */
export function sanitizeGatewayError(message: string, fallback: string): string {
  if (!GATEWAY_ERROR_BLOB.test(message)) {
    return message;
  }
  const jsonStart = message.indexOf('{');
  if (jsonStart === -1) {
    return fallback;
  }
  try {
    const parsed = JSON.parse(message.slice(jsonStart));
    const inner = parsed?.error?.message;
    return typeof inner === 'string' && inner.trim() ? inner : fallback;
  } catch {
    // Malformed payload — never surface the raw string.
    return fallback;
  }
}

/** Machine code the api puts in `message` when the location is closed for online parking (PB-7734). */
export const LOCATION_CLOSED_ERROR_CODE = 'GARAGE_CLOSED';

/**
 * Whether an API error is the api refusing a new online sale because the location is closed for
 * online parking — inactive, or under an active business closure (PB-7734).
 *
 * The api answers with HTTP 422 and a `RestError` whose `message` is `GARAGE_CLOSED` and whose
 * `developerMessage` is the parker-facing sentence (`GarageOnlineAvailability.refuseIfClosed`).
 *
 * ## Invariants:
 * - Exact match on the code, not a substring, and no status check. Why: it is a machine code the
 *   api sets verbatim, and every caller reads it only to decide which COPY to show — so a
 *   missed status cannot widen what the parker is allowed to do. Same shape as
 *   {@link readOpenCloseRefusal}.
 */
export function isLocationClosedRefusal(
  error: unknown
): error is { response: { data: { message: string; developerMessage?: unknown } } } {
  return (
    !!error &&
    typeof error === 'object' &&
    'response' in error &&
    !!error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    !!error.response.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data &&
    error.response.data.message === LOCATION_CLOSED_ERROR_CODE
  );
}

/**
 * The parker-facing sentence from a closed-location refusal (PB-7734), or `null` for any other
 * error — including a `GARAGE_CLOSED` refusal whose `developerMessage` is absent or empty.
 *
 * The one place a `GARAGE_CLOSED` refusal is turned into copy. Its readers are
 * {@link extractApiErrorMessage}, `getApiErrorMessage` (`@/lib/api/client-api`), and the screens
 * whose catch blocks show their own fixed copy rather than going through either:
 * `useExtendReservation.handleExtend`, `useExtendPayment.handlePaymentReady`,
 * `useTouchlessPayment.handlePaymentReady`, and `touchless.store.submitPayment`
 * (`paymentRefusal`).
 *
 * ## Invariants:
 * - Returns `null` rather than a fallback. Why: each caller keeps its own existing copy as the
 *   fallback (`'Payment failed. Please try again.'`, `'Failed to extend reservation'`, …), so a
 *   refusal the server did not explain reads exactly as it did before this change.
 */
export function readLocationClosedRefusal(error: unknown): string | null {
  if (!isLocationClosedRefusal(error)) return null;
  const sentence = error.response.data.developerMessage;
  return typeof sentence === 'string' && sentence.length > 0 ? sentence : null;
}

/** Machine code the api puts in `developerMessage` when a permit rate can't be sold (PB-5735). */
export const RATE_UNAVAILABLE_ERROR_CODE = 'PERMIT_RATE_UNAVAILABLE';

/**
 * Whether an API error is the api refusing a monthly purchase because the chosen permit rate is
 * unavailable — its usage limit is used up, or the rate pool it belongs to is full (PB-5735).
 *
 * The api answers with HTTP 400 and a `RestError` whose `message` is the parker-facing sentence
 * (so every reader of `message` keeps showing it) and whose `developerMessage` is
 * `PERMIT_RATE_UNAVAILABLE` (`PermitRateUnavailableException`, `ApplicationExceptionHandler`).
 *
 * ## Invariants:
 * - Exact code in `developerMessage` AND the 400 status. Why: a match tells the customer nothing
 *   was charged and steers them to another plan instead of Retry, so only the api's own refusal
 *   may say that — never the sentence (free text naming the card) or the code on another status.
 */
export function isRateUnavailableRefusal(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 400) return false;
  return (
    !!error &&
    typeof error === 'object' &&
    'response' in error &&
    !!error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    !!error.response.data &&
    typeof error.response.data === 'object' &&
    'developerMessage' in error.response.data &&
    error.response.data.developerMessage === RATE_UNAVAILABLE_ERROR_CODE
  );
}

/**
 * Extracts a user-facing error message from an Axios error response.
 * Falls back to the provided default message if no API message is available.
 * Any raw payment-gateway error blob is reduced to its human-readable message.
 *
 * ## Invariants:
 * - A `GARAGE_CLOSED` refusal (PB-7734) shows the server's `developerMessage` sentence, and the
 *   fallback when that sentence is absent — never the raw machine code. Why: the api carries the
 *   code in `message` and the parker-facing sentence beside it; the generic path below would
 *   print `GARAGE_CLOSED` to the customer. This covers only the callers of THIS function (the
 *   `/reserve` checkout, the monthly sign-up and quote forms, the parking-validation event
 *   reservation). The `/z` Pay path reads `getApiErrorMessage` instead, which applies the same
 *   mapping through {@link readLocationClosedRefusal}.
 */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (isLocationClosedRefusal(error)) {
    return readLocationClosedRefusal(error) ?? fallback;
  }
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  ) {
    return sanitizeGatewayError(error.response.data.message, fallback) || fallback;
  }
  return fallback;
}

/**
 * Detects the backend's 409 `identifier_conflict` error and returns which body
 * identifier collided, so the caller can render a friendly, field-specific
 * message instead of the raw code.
 *
 * The API surfaces this as a `RestError` with `message === 'identifier_conflict'`
 * and the identifier type in `developerMessage` (`"email"` / `"phone"`) — see
 * `UserProfileIdentifierConflictChecker.identifierConflict`. Unlike the user-upsert
 * endpoint, the reservation-by-event flow does not map it to a human-readable body,
 * so the raw `message` would otherwise reach the UI verbatim (PB-3418).
 *
 * @returns `'email'` or `'phone'` for a recognized identifier conflict, else `null`
 *   (including a 409 whose `developerMessage` is absent or an unrecognized type).
 */
export function getIdentifierConflictType(error: unknown): 'email' | 'phone' | null {
  if (getApiErrorStatus(error) !== 409) {
    return null;
  }
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data &&
    error.response.data.message === 'identifier_conflict' &&
    'developerMessage' in error.response.data
  ) {
    const type = error.response.data.developerMessage;
    if (type === 'email' || type === 'phone') {
      return type;
    }
  }
  return null;
}

/** Machine code the api puts in `message` for a closing-time refusal (PB-4395). */
export const CLOSING_ERROR_CODE = 'GARAGE_CLOSING';

/**
 * The parker-facing sentence from an open/close refusal, or `null` for any other error.
 *
 * The api refuses a booking or extension the device's schedule will not honour with a `RestError`
 * carrying `message === 'GARAGE_CLOSING'` and the sentence in `developerMessage` — which names the
 * closing time and, when spanning is permitted, both valid ends. `RestError` has no
 * `localizedMessage`, so the generic error path would otherwise show "something went wrong" and
 * discard a message the server took care to write.
 *
 * `errors[0]` carries the closing instant and `errors[1]` the reopening, as ISO strings, so a
 * caller can re-filter its own options without a second request.
 *
 * @returns the sentence to show, else `null` (including a GARAGE_CLOSING with no developerMessage)
 */
export function readOpenCloseRefusal(error: unknown): string | null {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data &&
    error.response.data.message === CLOSING_ERROR_CODE &&
    'developerMessage' in error.response.data
  ) {
    const sentence = error.response.data.developerMessage;
    if (typeof sentence === 'string' && sentence.length > 0) {
      return sentence;
    }
  }
  return null;
}

/** Machine code the api puts in `message` when the garage requires payment before checkout (PB-7891). */
export const VALET_PAYMENT_REQUIRED_CODE = 'VALET_PAYMENT_REQUIRED';

/**
 * Whether an API error is the corporate-validation check-out refusing because the ticket's balance
 * must be paid before the vehicle can be retrieved (PB-7985).
 *
 * `PUT /corporateValidation/update/ticket` refuses a CHECKOUT update with HTTP 422 and a `RestError`
 * whose `message` is `VALET_PAYMENT_REQUIRED` (`CorporateValidationServiceImpl`, `TicketServiceImpl`).
 * Without this check `extractApiErrorMessage` shows the raw code to the user.
 *
 * ## Invariants:
 * - Requires BOTH the 422 status and the exact code. Why: 422 is a generic validation refusal other
 *   guards also return, and the code on any other status is not this refusal — treating either
 *   alone as "pay first" would hide a real error behind the wrong remedy.
 * - Exact match on the code, not a substring. Why: it is a machine code the api sets verbatim,
 *   unlike the free-text captcha messages.
 */
export function isValetPaymentRequired(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 422) return false;
  return (
    !!error &&
    typeof error === 'object' &&
    'response' in error &&
    !!error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    !!error.response.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data &&
    error.response.data.message === VALET_PAYMENT_REQUIRED_CODE
  );
}

/** Machine code the api puts in `data.code` when the location has not gone live yet (PB-7746). */
export const GARAGE_NOT_OPEN_ERROR_CODE = 'GARAGE_NOT_OPEN';

/**
 * Whether an API error is the api refusing because the location is still onboarding and not yet
 * open for online parking (PB-7746 / PB-7765).
 *
 * The api answers HTTP 404 with a `GarageNotOpenResponse` body `{ code, garageId, message }`
 * (`ApplicationExceptionHandler`). Since PB-8745 the body may also carry `garageName` and an
 * `address` object (`street`, `city`, `state`, `zip`) — only when the request comes from a site that
 * serves the garage; this detector ignores them (read them with `getGarageNotOpenDetails`). Never
 * throws.
 *
 * ## Invariants:
 * - HTTP 404 AND exact `response.data.code`; never the `message`. Why: this is the ONLY detector
 *   of the not-open state, so a looser match would show "not open yet" for a location that is
 *   closed (`GARAGE_CLOSED`, a 422 carrying its code in `message`) or for a plain 404, and a
 *   stricter one would hide the state. The code is in `data.code`, unlike `GARAGE_CLOSED`.
 * - Every caller that must recognise not-open uses this helper (`useDirectGarage` probe,
 *   `qr.store.fetchRate`, the `/z` zone probe, `useZoneSuggestions`). The `/z` gate-arm load is
 *   deliberately blind to onboarding (POS shares it), so it cannot report this state itself.
 */
export function isGarageNotOpenRefusal(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 404) return false;
  return (
    !!error &&
    typeof error === 'object' &&
    'response' in error &&
    !!error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    !!error.response.data &&
    typeof error.response.data === 'object' &&
    'code' in error.response.data &&
    error.response.data.code === GARAGE_NOT_OPEN_ERROR_CODE
  );
}

/** Display-ready location details carried on a not-open refusal (PB-8745). */
export interface GarageNotOpenDetails {
  /** The location's name, trimmed; absent when the body has none. */
  garageName?: string;
  /** The location's address as one line (`street, city, state, zip`); absent when empty. */
  garageAddress?: string;
}

/**
 * Reads the onboarding location's name and address off a not-open refusal (PB-8745), ready to
 * display. Returns `{}` for anything that is not `isGarageNotOpenRefusal`, and for a refusal whose
 * body carries no usable details. Never throws.
 *
 * ## Invariants:
 * - Gated on `isGarageNotOpenRefusal` first. Why: a closed location's 422 or a plain 404 must never
 *   be dressed up as a not-open location card, however its body happens to be shaped.
 * - Strings only, trimmed, blank dropped; `address` only when it is a plain object. Why: the body
 *   is untyped server output rendered on a customer page, so a number, array or nested object is
 *   ignored rather than coerced into text. Absent details are the normal legacy body (api not yet
 *   deployed, or a request from outside the garage's site scope) and mean "render the banner only".
 * - The address line is joined by `buildGarageAddress`, not a local copy. Why: the `/z` not-open
 *   screen shows the same garage address through it, and both pages must read identically.
 */
export function getGarageNotOpenDetails(error: unknown): GarageNotOpenDetails {
  if (!isGarageNotOpenRefusal(error)) return {};
  // isGarageNotOpenRefusal has proven error.response.data is a non-null object.
  const data = (error as { response: { data: Record<string, unknown> } }).response.data;
  const details: GarageNotOpenDetails = {};

  const garageName = trimmedString(data.garageName);
  if (garageName) details.garageName = garageName;

  const address = data.address;
  if (isPlainObject(address)) {
    const garageAddress = buildGarageAddress({
      address: trimmedString(address.street),
      city: trimmedString(address.city),
      state: trimmedString(address.state),
      zip: trimmedString(address.zip),
    });
    if (garageAddress) details.garageAddress = garageAddress;
  }
  return details;
}

function trimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/** Returns the HTTP status from an Axios error, or undefined for non-HTTP errors. */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status;
  }
  return undefined;
}

const GATEWAY_GAVE_UP_STATUSES = new Set([502, 503, 504]);

// No answer from the API itself, so the request may still have succeeded server-side.
export function isOutcomeUnknownError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  if (!error.response) return true;
  return GATEWAY_GAVE_UP_STATUSES.has(error.response.status);
}

/**
 * Whether an API error is the server refusing guest checkout for this device (PB-3894).
 *
 * ## Invariants:
 * - Matched on the `GUEST_CHECKOUT_NOT_ENABLED` code the API embeds in the message, not on the
 *   403 status alone. Why: 403 is a generic refusal that other guards also return, and treating
 *   every 403 as "please sign in" would send a customer to a login screen for an unrelated
 *   permission failure.
 * - This means the operator changed the setting between the scan and the payment, so the right
 *   response is the sign-in prompt rather than a generic error toast.
 */
/**
 * Whether an API error is the backend rejecting the reCAPTCHA token (PB-3894).
 *
 * The API raises `RecaptchaValidationException`, which extends `ReservationNotFoundException`
 * and so lands on the shared `400 BAD_REQUEST` handler with the raw text in `message`. Three
 * wordings are in circulation across the endpoints that validate a token:
 * `"Captcha Validation Failed"`, `"Error occurred during Captcha Validation"` and
 * `"Error occurs during Captcha Validation"` (`TicketServiceImpl`, `AuthenticationServiceImpl`,
 * `ReservationServiceImpl`, `AccessGroupController`, `CorporateValidationController`).
 *
 * ## Invariants:
 * - Matched on the word "captcha", case-insensitively — never on an exact string. Why: the
 *   three wordings differ by a typo ("occurs"/"occurred") and there is no error code to key
 *   on, so an exact match would silently stop retrying whenever a caller hit a different one.
 * - Requires the 400 status as well as the text. Why: "captcha" could appear in an unrelated
 *   message, and retrying a non-reCAPTCHA failure just charges the customer's patience twice.
 */
export function isRecaptchaValidationFailure(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 400) return false;
  if (!error || typeof error !== 'object' || !('response' in error)) return false;
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data as { message?: string } | undefined;
  return typeof data?.message === 'string' && /captcha/i.test(data.message);
}

const BOOKING_RULE_REJECTION_MESSAGES = new Set(['you can only have one active permit']);

/**
 * Whether an API error is a booking-policy refusal rather than a payment failure (PB-3329).
 *
 * ## Invariants:
 * - Allowlist only: declines share the same 400 with no error code, so unknown messages stay on the payment path and keep Retry.
 */
export function isBookingRuleRejection(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 400) return false;
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data as { message?: string } | undefined;
  return (
    typeof data?.message === 'string' &&
    BOOKING_RULE_REJECTION_MESSAGES.has(data.message.trim().toLowerCase())
  );
}

export function isGuestCheckoutRefused(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('response' in error)) return false;
  const response = (error as { response?: { status?: number; data?: unknown } }).response;
  if (!response || response.status !== 403) return false;
  const data = response.data as { message?: string } | undefined;
  return typeof data?.message === 'string' && data.message.includes('GUEST_CHECKOUT_NOT_ENABLED');
}
